import * as THREE from 'three';
import { getBuildingGraph } from './visualization';
import { extractExteriorWallsFromRooms, generateFacadeGridPoints } from './facadeWallPolygons';

/**
 * GridPoint type for representing measurement points on heatmap surfaces
 */
export type HeatmapGridPoint = {
	point: THREE.Vector3;
	normal: THREE.Vector3;
	key: string;
	group: 'floor' | 'roof' | 'facade' | 'terrain';
	floorZ: number;
	roofZ: number;
};

/**
 * Internal cache for facade grid points to avoid redundant computation
 */
let photonHeatmapFacadeGridCache: {
	key: string;
	points: HeatmapGridPoint[];
} | null = null;

/**
 * Internal cache for terrain grid points to avoid redundant computation
 */
let photonHeatmapTerrainGridCache: {
	key: string;
	points: HeatmapGridPoint[];
} | null = null;

/**
 * Internal cache for horizontal surface (floor/roof) grid points
 */
let photonHeatmapHorizontalSurfaceGridCache: {
	key: string;
	points: HeatmapGridPoint[];
} | null = null;

/**
 * Clears all internal grid caches when the scene changes
 */
export function clearGridCaches(): void {
	photonHeatmapFacadeGridCache = null;
	photonHeatmapTerrainGridCache = null;
	photonHeatmapHorizontalSurfaceGridCache = null;
}

/**
 * Point-in-polygon test using ray casting algorithm
 */
function isPointInPolygon2D(point: THREE.Vector2, polygon: THREE.Vector2[]): boolean {
	let inside = false;
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i].x;
		const yi = polygon[i].y;
		const xj = polygon[j].x;
		const yj = polygon[j].y;
		const px = point.x;
		const py = point.y;

		const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
		if (intersect) inside = !inside;
	}
	return inside;
}

/**
 * Retrieves the 2D polygon of the site limit from the scene
 */
function getSiteLimitPolygon2D(scene: THREE.Scene): THREE.Vector2[] {
	const siteLimit = scene.getObjectByName('Site Limit');
	if (!(siteLimit instanceof THREE.Line)) {
		return [];
	}

	const positionAttr = siteLimit.geometry.getAttribute('position');
	if (!positionAttr) {
		return [];
	}

	const polygon: THREE.Vector2[] = [];
	for (let i = 0; i < positionAttr.count; i++) {
		const vertex = new THREE.Vector3().fromBufferAttribute(positionAttr, i);
		vertex.applyMatrix4(siteLimit.matrixWorld);
		polygon.push(new THREE.Vector2(vertex.x, vertex.y));
	}

	if (polygon.length > 1 && polygon[0].distanceTo(polygon[polygon.length - 1]) < 1e-6) {
		polygon.pop();
	}

	return polygon;
}

/**
 * Builds grid points on horizontal surfaces (floors and roofs) using ray tracing
 */
function buildBuildingHorizontalGridPoints(
	scene: THREE.Scene,
	gridSize: number
): HeatmapGridPoint[] {
	const siteLimitPolygon = getSiteLimitPolygon2D(scene);
	if (siteLimitPolygon.length < 3) {
		return [];
	}

	const buildingMeshes: THREE.Mesh[] = [];
	scene.traverse((obj) => {
		if (obj instanceof THREE.Mesh && obj.userData?.category === 'building') {
			buildingMeshes.push(obj);
		}
	});
	if (buildingMeshes.length === 0) {
		return [];
	}

	const cacheKey = `${scene.uuid}|horizontal|${gridSize}|${buildingMeshes.length}|${siteLimitPolygon.length}`;
	if (photonHeatmapHorizontalSurfaceGridCache?.key === cacheKey) {
		return photonHeatmapHorizontalSurfaceGridCache.points;
	}

	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;
	for (const p of siteLimitPolygon) {
		if (p.x < minX) minX = p.x;
		if (p.x > maxX) maxX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.y > maxY) maxY = p.y;
	}

	const sceneBounds = new THREE.Box3();
	for (const mesh of buildingMeshes) {
		sceneBounds.expandByObject(mesh);
	}
	const rayStartZ = sceneBounds.max.z + 50;
	const rayDirection = new THREE.Vector3(0, 0, -1);
	const raycaster = new THREE.Raycaster();

	const width = maxX - minX;
	const depth = maxY - minY;
	const cellsX = Math.max(1, Math.ceil(width / gridSize));
	const cellsY = Math.max(1, Math.ceil(depth / gridSize));

	const points: HeatmapGridPoint[] = [];
	const seen = new Set<string>();
	for (let x = 0; x < cellsX; x++) {
		for (let y = 0; y < cellsY; y++) {
			const worldX = minX + (x + 0.5) * (width / cellsX);
			const worldY = minY + (y + 0.5) * (depth / cellsY);
			const sample2D = new THREE.Vector2(worldX, worldY);
			if (!isPointInPolygon2D(sample2D, siteLimitPolygon)) {
				continue;
			}

			raycaster.set(new THREE.Vector3(worldX, worldY, rayStartZ), rayDirection);
			const intersects = raycaster.intersectObjects(buildingMeshes, true);
			if (intersects.length === 0) {
				continue;
			}

			for (const hit of intersects) {
				const worldNormal = hit.face?.normal
					? hit.face.normal
						.clone()
						.applyMatrix3(new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld))
						.normalize()
					: null;
				if (!worldNormal) continue;
				if (Math.abs(worldNormal.z) < 0.75) continue;

				const zBucket = Math.round(hit.point.z / Math.max(gridSize * 0.5, 0.25));
				const key = `${x}_${y}_${zBucket}`;
				if (seen.has(key)) continue;
				seen.add(key);

				const group: HeatmapGridPoint['group'] = worldNormal.z >= 0 ? 'floor' : 'roof';
				points.push({
					point: hit.point.clone(),
					normal: worldNormal,
					key: `${group}_${x}_${y}_${zBucket}`,
					group,
					floorZ: hit.point.z,
					roofZ: hit.point.z,
				});
			}
		}
	}

	photonHeatmapHorizontalSurfaceGridCache = {
		key: cacheKey,
		points,
	};

	return points;
}

/**
 * Builds grid points on horizontal surfaces (floors and roofs) from room polygons or buildings
 */
export function buildDailyFloorHeatmapGridPoints(
	scene: THREE.Scene,
	gridSize: number
): HeatmapGridPoint[] {
	const roomGroup = scene.getObjectByName('RoomFloorPolygonsGroup') as THREE.Group | null;
	if (!roomGroup) return buildBuildingHorizontalGridPoints(scene, gridSize);

	const points: HeatmapGridPoint[] = [];
	for (const room of roomGroup.children) {
		const basePolygon: { x: number; y: number }[] | undefined = room.userData.basePolygon;
		if (!basePolygon || basePolygon.length < 3) continue;

		let minX = Infinity;
		let maxX = -Infinity;
		let minY = Infinity;
		let maxY = -Infinity;
		for (const p of basePolygon) {
			if (p.x < minX) minX = p.x;
			if (p.x > maxX) maxX = p.x;
			if (p.y < minY) minY = p.y;
			if (p.y > maxY) maxY = p.y;
		}

		const roomWidth = maxX - minX;
		const roomDepth = maxY - minY;
		const cellsX = Math.max(1, Math.ceil(roomWidth / gridSize));
		const cellsY = Math.max(1, Math.ceil(roomDepth / gridSize));
		const cellWidth = roomWidth / cellsX;
		const cellDepth = roomDepth / cellsY;

		const bbox = new THREE.Box3().setFromObject(room);
		const measurementZ = bbox.min.z + 0.1;
		const roofMeasurementZ = bbox.max.z - 0.1;

		const polygonLocal = basePolygon.map((p) => new THREE.Vector2(p.x, p.y));
		for (let x = 0; x < cellsX; x++) {
			for (let y = 0; y < cellsY; y++) {
				const localCenterX = minX + (x + 0.5) * cellWidth;
				const localCenterY = minY + (y + 0.5) * cellDepth;
				const localPointXY = new THREE.Vector2(localCenterX, localCenterY);
				if (!isPointInPolygon2D(localPointXY, polygonLocal)) continue;

				const worldPoint = new THREE.Vector3(localCenterX, localCenterY, 0).applyMatrix4(
					room.matrixWorld
				);
				worldPoint.z = measurementZ;

				points.push({
					point: worldPoint,
					normal: new THREE.Vector3(0, 0, 1),
					key: `floor_${room.uuid}_${x}_${y}`,
					group: 'floor',
					floorZ: measurementZ,
					roofZ: roofMeasurementZ,
				});

				points.push({
					point: new THREE.Vector3(worldPoint.x, worldPoint.y, roofMeasurementZ),
					normal: new THREE.Vector3(0, 0, -1),
					key: `roof_${room.uuid}_${x}_${y}`,
					group: 'roof',
					floorZ: roofMeasurementZ,
					roofZ: roofMeasurementZ,
				});
			}
		}
	}

	if (points.length === 0) {
		return buildBuildingHorizontalGridPoints(scene, gridSize);
	}

	return points;
}

/**
 * Retrieves building targets for facade heatmap generation
 */
function getFacadeHeatmapBuildingTargets(scene: THREE.Scene): THREE.Object3D[] {
	const targets: THREE.Object3D[] = [];
	const seenPaths = new Set<string>();

	scene.traverse((obj) => {
		const path = obj.userData?.path as string | undefined;
		if (!path || seenPaths.has(path)) {
			return;
		}

		const isBuildingCategory = obj.userData?.category === 'building';
		const isNamedBuilding = typeof obj.name === 'string' && obj.name.startsWith('building_');
		const hasBuildingChildren = obj.children.some(
			(child) => child instanceof THREE.Mesh && child.userData?.category === 'building'
		);

		if (!isBuildingCategory && !isNamedBuilding && !hasBuildingChildren) {
			return;
		}

		seenPaths.add(path);
		targets.push(obj);
	});

	return targets;
}

/**
 * Builds grid points on building facades
 */
export async function buildFacadeHeatmapGridPoints(
	scene: THREE.Scene,
	gridSize: number
): Promise<HeatmapGridPoint[]> {
	const buildingTargets = getFacadeHeatmapBuildingTargets(scene);
	if (buildingTargets.length === 0) {
		return [];
	}

	const targetPaths = buildingTargets
		.map((target) => String(target.userData?.path ?? target.uuid))
		.sort()
		.join('|');
	const cacheKey = `${scene.uuid}|facade|${gridSize}|${targetPaths}`;
	if (photonHeatmapFacadeGridCache?.key === cacheKey) {
		return photonHeatmapFacadeGridCache.points;
	}

	const allFacadePoints: HeatmapGridPoint[] = [];

	for (const buildingTarget of buildingTargets) {
		try {
			const graphBuilding = await getBuildingGraph(buildingTarget);
			if (!graphBuilding) {
				continue;
			}

			const wallPolygons = extractExteriorWallsFromRooms(buildingTarget, graphBuilding);
			const facadeGridPoints = generateFacadeGridPoints(wallPolygons, gridSize);
			const buildingPath = String(buildingTarget.userData?.path ?? buildingTarget.uuid);

			facadeGridPoints.forEach((gridPoint, index) => {
				allFacadePoints.push({
					point: new THREE.Vector3(gridPoint.x, gridPoint.y, gridPoint.z),
					normal: new THREE.Vector3(
						gridPoint.normal.x,
						gridPoint.normal.y,
						gridPoint.normal.z
					).normalize(),
					key: `facade_${buildingPath}_${gridPoint.wallId ?? 'wall'}_${index}`,
					group: 'facade',
					floorZ: gridPoint.z,
					roofZ: gridPoint.z,
				});
			});
		} catch (error) {
			console.warn('Failed to build facade heatmap points for building target:', error);
		}
	}

	photonHeatmapFacadeGridCache = {
		key: cacheKey,
		points: allFacadePoints,
	};

	return allFacadePoints;
}

/**
 * Builds grid points on terrain surface
 */
export function buildTerrainHeatmapGridPoints(
	scene: THREE.Scene,
	gridSize: number
): HeatmapGridPoint[] {
	const terrainObject = scene.getObjectByName('terrain');
	if (!terrainObject) {
		return [];
	}

	const siteLimitPolygon = getSiteLimitPolygon2D(scene);
	if (siteLimitPolygon.length < 3) {
		return [];
	}

	const cacheKey = `${scene.uuid}|terrain|${gridSize}`;
	if (photonHeatmapTerrainGridCache?.key === cacheKey) {
		return photonHeatmapTerrainGridCache.points;
	}

	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;
	for (const p of siteLimitPolygon) {
		if (p.x < minX) minX = p.x;
		if (p.x > maxX) maxX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.y > maxY) maxY = p.y;
	}

	const terrainBounds = new THREE.Box3().setFromObject(terrainObject);
	const rayStartZ = terrainBounds.max.z + 100;
	const rayDirection = new THREE.Vector3(0, 0, -1);
	const raycaster = new THREE.Raycaster();

	const width = maxX - minX;
	const depth = maxY - minY;
	const cellsX = Math.max(1, Math.ceil(width / gridSize));
	const cellsY = Math.max(1, Math.ceil(depth / gridSize));

	const points: HeatmapGridPoint[] = [];
	let index = 0;
	for (let x = 0; x < cellsX; x++) {
		for (let y = 0; y < cellsY; y++) {
			const worldX = minX + (x + 0.5) * (width / cellsX);
			const worldY = minY + (y + 0.5) * (depth / cellsY);
			const sample2D = new THREE.Vector2(worldX, worldY);
			if (!isPointInPolygon2D(sample2D, siteLimitPolygon)) {
				continue;
			}

			raycaster.set(new THREE.Vector3(worldX, worldY, rayStartZ), rayDirection);
			const intersects = raycaster.intersectObject(terrainObject, true);
			if (intersects.length === 0) {
				continue;
			}

			const hit = intersects[0];
			const normal = hit.face?.normal
				? hit.face.normal
					.clone()
					.applyMatrix3(new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld))
					.normalize()
				: new THREE.Vector3(0, 0, 1);

			points.push({
				point: hit.point.clone(),
				normal,
				key: `terrain_${index}`,
				group: 'terrain',
				floorZ: hit.point.z,
				roofZ: hit.point.z,
			});
			index++;
		}
	}

	photonHeatmapTerrainGridCache = {
		key: cacheKey,
		points,
	};

	return points;
}
